import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/client/lib/utils';
import { apiFetch } from '@/client/lib/api';
import { toast } from 'sonner';
import { SettingsLayout } from '@/client/components/layout/settings-layout';

interface NotificationSettings {
  milestoneEnabled: boolean;
  milestoneThresholds: number[];
  digestEnabled: boolean;
  digestFrequency: 'daily' | 'weekly' | 'monthly';
  digestDay: number;
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DEFAULT_THRESHOLDS = [100, 500, 1000, 5000, 10000];

export function NotificationsSettingsPage() {
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customThresholds, setCustomThresholds] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiFetch('/api/settings/notifications');
      const data = await response.json();
      setSettings(data.settings);
      setCustomThresholds(data.settings.milestoneThresholds.join(', '));
    } catch {
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (updates: Partial<NotificationSettings>) => {
    if (!settings) return;

    setSaving(true);
    try {
      const response = await apiFetch('/api/settings/notifications', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      setSettings(data.settings);
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleMilestoneToggle = (enabled: boolean) => {
    setSettings((prev) => (prev ? { ...prev, milestoneEnabled: enabled } : null));
    saveSettings({ milestoneEnabled: enabled });
  };

  const handleDigestToggle = (enabled: boolean) => {
    setSettings((prev) => (prev ? { ...prev, digestEnabled: enabled } : null));
    saveSettings({ digestEnabled: enabled });
  };

  const handleFrequencyChange = (frequency: 'daily' | 'weekly' | 'monthly') => {
    setSettings((prev) => (prev ? { ...prev, digestFrequency: frequency } : null));
    saveSettings({ digestFrequency: frequency });
  };

  const handleDayChange = (day: number) => {
    setSettings((prev) => (prev ? { ...prev, digestDay: day } : null));
    saveSettings({ digestDay: day });
  };

  const handleThresholdsChange = () => {
    const thresholds = customThresholds
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n) && n > 0)
      .sort((a, b) => a - b);

    if (thresholds.length === 0) {
      toast.error('Please enter at least one valid threshold');
      return;
    }

    setSettings((prev) => (prev ? { ...prev, milestoneThresholds: thresholds } : null));
    setCustomThresholds(thresholds.join(', '));
    saveSettings({ milestoneThresholds: thresholds });
  };

  const resetThresholds = () => {
    setCustomThresholds(DEFAULT_THRESHOLDS.join(', '));
    setSettings((prev) => (prev ? { ...prev, milestoneThresholds: DEFAULT_THRESHOLDS } : null));
    saveSettings({ milestoneThresholds: DEFAULT_THRESHOLDS });
  };

  if (loading) {
    return (
      <SettingsLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout>
      {settings && (
        <>
          {/* Milestone Notifications */}
          <div className="rounded-lg border bg-card p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold">Milestone Notifications</h2>
                <p className="text-sm text-muted-foreground">
                  Get notified when your links hit click milestones.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.milestoneEnabled}
                  onChange={(e) => handleMilestoneToggle(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
              </label>
            </div>

            {settings.milestoneEnabled && (
              <div className="space-y-4 pt-4 border-t">
                <div>
                  <label className="block text-sm font-medium mb-2">Milestone Thresholds</label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Enter click counts separated by commas. You&apos;ll be notified when each
                    threshold is reached.
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customThresholds}
                      onChange={(e) => setCustomThresholds(e.target.value)}
                      placeholder="100, 500, 1000, 5000, 10000"
                      className="flex-1 max-w-md px-4 py-2 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    />
                    <button
                      onClick={handleThresholdsChange}
                      disabled={saving}
                      className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      onClick={resetThresholds}
                      disabled={saving}
                      className="px-4 py-2 rounded-md border text-sm font-medium hover:bg-muted disabled:opacity-50"
                    >
                      Reset
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {settings.milestoneThresholds.map((threshold) => (
                    <span
                      key={threshold}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-muted"
                    >
                      {threshold.toLocaleString()} clicks
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Weekly Digest */}
          <div className="rounded-lg border bg-card p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold">Performance Digest</h2>
                <p className="text-sm text-muted-foreground">
                  Receive a summary of your link performance.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.digestEnabled}
                  onChange={(e) => handleDigestToggle(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
              </label>
            </div>

            {settings.digestEnabled && (
              <div className="space-y-4 pt-4 border-t">
                <div>
                  <label className="block text-sm font-medium mb-2">Frequency</label>
                  <div className="flex gap-2">
                    {(['daily', 'weekly', 'monthly'] as const).map((freq) => (
                      <button
                        key={freq}
                        onClick={() => handleFrequencyChange(freq)}
                        className={cn(
                          'px-4 py-2 rounded-md text-sm font-medium transition-colors',
                          settings.digestFrequency === freq
                            ? 'bg-primary text-primary-foreground'
                            : 'border hover:bg-muted'
                        )}
                      >
                        {freq.charAt(0).toUpperCase() + freq.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {settings.digestFrequency === 'weekly' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Delivery Day</label>
                    <select
                      value={settings.digestDay}
                      onChange={(e) => handleDayChange(parseInt(e.target.value, 10))}
                      className="px-4 py-2 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    >
                      {DAYS.map((day, index) => (
                        <option key={day} value={index}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  {settings.digestFrequency === 'daily'
                    ? 'You will receive a digest every day at 9:00 AM (your local time).'
                    : settings.digestFrequency === 'weekly'
                      ? `You will receive a digest every ${DAYS[settings.digestDay]} at 9:00 AM (your local time).`
                      : 'You will receive a digest on the 1st of each month at 9:00 AM (your local time).'}
                </p>
              </div>
            )}
          </div>

          {/* Info Section */}
          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            <p>
              <strong>Note:</strong> Email notifications are sent to your account email address.
              Milestone notifications are sent once per link per threshold and won&apos;t repeat.
            </p>
          </div>
        </>
      )}
    </SettingsLayout>
  );
}
